/* eslint-disable eqeqeq */
import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Select from '@mui/material/Select';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getJobs, mergeJobsArAp, updateJob as updateJobInStore } from '../../../store/slices/jobsSlice';
import { getClients } from '../../../store/slices/clientsSlice';
import AddJobDialog from './AddJobDialog';
import JobDetailsDialog from './JobDetailsDialog';
import DeletedJobDetailsDialog from './DeletedJobDetailsDialog';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import { getUser } from '../../../store/slices/userSlice';
import { getUsersList } from '../../../store/slices/usersSlice';
import { formatUserDisplayName } from '../../../utils/userDisplayName';
import { routes, states, api } from '../../../utils/constants';
import {
  ACTIVE_JOB_STATUS_IDS,
  ARCHIVED_JOB_STATUS_IDS,
  JOB_DONE_VIEW_STATUS_IDS,
  PRIORITY_JOB_STATUS_IDS,
  getStatusLabel,
  getStatusOptions,
  getStatusChipStyles,
  getStatusFilterChipSx,
} from '../../../utils/jobStatuses';
import { getPriorityLabel, PRIORITY_OPTIONS } from '../../../utils/jobPriorities';
import { formatSlaTableDisplay } from '../../../utils/jobSla';
import { formatShortDateTime } from '../../../utils/jobLogFormatters';
import { getClientChipStyles } from '../../../utils/clientColors';
import { fetchJobTags, getTagChipSx } from '../../../utils/jobTagSuggestions';
import { GET, PATCH } from '../../../configs/api';
import {
  canEditJobNotes as canEditJobNotesPermission,
  canCreateOrEditJob,
  canMarkJobPaid,
  isOnlyDispatcher,
  canViewDeletedJobs,
  primaryRole,
  ROLES,
} from '../../../utils/permissions';
import {
  GRID_PREFERENCES_STORAGE_KEY,
  DEFAULT_GRID_PREFERENCE,
  parseGridPreferences,
  sanitizeGridPreference,
  applyColumnOrder,
  JobsColumnsPopover,
} from './JobsColumnsHelper';
import { useJobStatusColorsRevision } from '../../../hooks/useJobStatusColorsRevision';
import useIsMobile from '../../../hooks/useIsMobile';
import { scaleColumnsForMobile } from '../../../utils/mobileDataGrid';
import JobsSavedSearchesBar from './JobsSavedSearchesBar';
import {
  arraysEqualById,
  captureFilterSnapshot,
  clearDefaultSearch,
  deleteSearch,
  EMPTY_FILTER_SNAPSHOT,
  findSearchById,
  getDefaultSearchForView,
  getViewBucket,
  loadJobsSavedSearches,
  parseDateField,
  pruneSavedSearchesAgainstClients,
  renameSearch,
  sanitizeFilterSnapshot,
  setDefaultSearch,
  snapshotsMatch,
  upsertSearch,
} from './JobsSavedSearchesHelper';

const paginationModel = { page: 0, pageSize: 30 };
const TOP_FILTERS_STORAGE_KEY = 'jobsTopFiltersExpanded';
const SECONDARY_FILTERS_STORAGE_KEY = 'jobsSecondaryFiltersExpanded';
const TOP_FILTERS_MOBILE_STORAGE_KEY = 'jobsTopFiltersExpandedMobile';
const SECONDARY_FILTERS_MOBILE_STORAGE_KEY = 'jobsSecondaryFiltersExpandedMobile';
const VIEW_MODE_STORAGE_KEY = 'jobsViewMode';
const VALID_VIEW_MODES = new Set(['description', 'ar-ap']);
/** Matches MUI `breakpoints.down('md')` / useIsMobile (max-width 899.95px). */
const MOBILE_VIEWPORT_MEDIA_QUERY = '(max-width: 899.95px)';

const getIsMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY).matches;

const readFiltersExpandedPreference = (desktopKey, mobileKey, isMobileViewport) => {
  const key = isMobileViewport ? mobileKey : desktopKey;
  const storedValue = window.localStorage.getItem(key);
  if (storedValue === null) return !isMobileViewport; // collapsed on mobile by default
  return storedValue === 'true';
};

const readViewModePreference = () => {
  const storedValue = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return VALID_VIEW_MODES.has(storedValue) ? storedValue : 'description';
};

const JOBS_PATH_TO_VIEW = {
  [routes.jobsAll]: 'all',
  [routes.jobsActive]: 'active',
  [routes.jobsArchived]: 'archived',
  [routes.jobsJobDone]: 'jobDone',
  [routes.jobsPriority]: 'priority',
  [routes.jobsDeleted]: 'deleted',
};

const JOBS_QUICK_NAV_TABS = [
  { viewKey: 'priority', label: 'Priority', path: routes.jobsPriority },
  { viewKey: 'active', label: 'Active', path: routes.jobsActive },
  { viewKey: 'jobDone', label: 'Job Done', path: routes.jobsJobDone },
  { viewKey: 'archived', label: 'Archived', path: routes.jobsArchived },
  { viewKey: 'all', label: 'All', path: routes.jobsAll },
];

const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.type === 'vendor') {
    return tech.company_name || '';
  }
  return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

const getJobTechnicianNames = (job) => {
  if (!Array.isArray(job?.technicians)) return [];
  return job.technicians.map(getTechnicianDisplayName).filter(Boolean);
};

const parseGridNumber = (value) => {
  if (value == null || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const numericFieldValueGetter = (field) => (_value, row) => parseGridNumber(row[field]);

export default function Jobs() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const companyId = user?.company_id ?? null;
  const isPrimaryAdmin = primaryRole(user) === ROLES.ADMIN;
  const usersList = useSelector(getUsersList);
  const { jobs, loading, error } = useSelector(getJobs);
  const clientsData = useSelector(getClients);
  // Ready only after a successful load — error payloads set loading=false with clients=[].
  const clientsReady = clientsData?.loading === false && !clientsData?.error;

  const displayUserByEmail = React.useMemo(() => {
    const byEmail = new Map(
      (usersList || [])
        .filter((u) => u.email)
        .map((u) => [u.email.toLowerCase(), u]),
    );
    return (email) => {
      if (!email) return '';
      const match = byEmail.get(String(email).toLowerCase());
      return match ? formatUserDisplayName(match) : email;
    };
  }, [usersList]);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedJob, setSelectedJob] = React.useState(null);
  const [initialJobDetailsTabValue, setInitialJobDetailsTabValue] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [columnsPanelAnchorEl, setColumnsPanelAnchorEl] = React.useState(null);
  
  // Filter state
  const [selectedStatuses, setSelectedStatuses] = React.useState([]);
  const [selectedPriorities, setSelectedPriorities] = React.useState([]);
  const [selectedStates, setSelectedStates] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('all');
  const [selectedTrades, setSelectedTrades] = React.useState([]);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [tagFilterOptions, setTagFilterOptions] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [selectedDateStart, setSelectedDateStart] = React.useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = React.useState(null);
  const [unlockedOnly, setUnlockedOnly] = React.useState(false);
  const [topFiltersExpanded, setTopFiltersExpanded] = React.useState(() =>
    readFiltersExpandedPreference(
      TOP_FILTERS_STORAGE_KEY,
      TOP_FILTERS_MOBILE_STORAGE_KEY,
      getIsMobileViewport(),
    ),
  );
  const [statusFilterExpanded, setStatusFilterExpanded] = React.useState(() =>
    readFiltersExpandedPreference(
      SECONDARY_FILTERS_STORAGE_KEY,
      SECONDARY_FILTERS_MOBILE_STORAGE_KEY,
      getIsMobileViewport(),
    ),
  );
  const [savedSearchesStorage, setSavedSearchesStorage] = React.useState(() => loadJobsSavedSearches(companyId));
  const [activeSavedSearchId, setActiveSavedSearchId] = React.useState(null);
  const suppressCustomDetectionRef = React.useRef(false);
  const loadedCompanyIdRef = React.useRef(companyId);
  const isMobileRef = React.useRef(isMobile);
  isMobileRef.current = isMobile;
  const skipViewportFilterSyncRef = React.useRef(true);
  
  // AR-AP view state (loading/error only; data lives in Redux jobs)
  const [viewMode, setViewMode] = React.useState(readViewModePreference); // 'description' | 'ar-ap'
  const [arApLoading, setArApLoading] = React.useState(false);
  const [arApError, setArApError] = React.useState(null);
  const [gridPreferences, setGridPreferences] = React.useState(() => parseGridPreferences());
  const [isNotesDialogOpen, setIsNotesDialogOpen] = React.useState(false);
  const [editingNotesJobId, setEditingNotesJobId] = React.useState(null);
  const [notesDraft, setNotesDraft] = React.useState('');
  const [notesUpdating, setNotesUpdating] = React.useState(false);
  const [notesDialogError, setNotesDialogError] = React.useState('');
  const [loadingPaid, setLoadingPaid] = React.useState({});
  const [deletedJobs, setDeletedJobs] = React.useState([]);
  const [deletedJobsLoading, setDeletedJobsLoading] = React.useState(false);
  const [deletedJobsError, setDeletedJobsError] = React.useState(null);
  const [deletedJobDetailError, setDeletedJobDetailError] = React.useState(null);
  const [selectedDeletedJob, setSelectedDeletedJob] = React.useState(null);
  const [deletedSearchQuery, setDeletedSearchQuery] = React.useState('');
  const jobsView = JOBS_PATH_TO_VIEW[location.pathname] || 'all';
  const isDeletedView = jobsView === 'deleted';
  const canViewDeleted = canViewDeletedJobs(user);
  const statusColorsRevision = useJobStatusColorsRevision();

  const jobsViewStatusIds = React.useMemo(() => {
    switch (jobsView) {
      case 'active':
        return ACTIVE_JOB_STATUS_IDS;
      case 'archived':
        return ARCHIVED_JOB_STATUS_IDS;
      case 'priority':
        return PRIORITY_JOB_STATUS_IDS;
      case 'jobDone':
        return JOB_DONE_VIEW_STATUS_IDS;
      case 'all':
      default:
        return null;
    }
  }, [jobsView]);

  const statusOptionsForCurrentView = React.useMemo(() => {
    const allOptions = getStatusOptions();
    if (!jobsViewStatusIds) return allOptions;
    return allOptions.filter((option) => jobsViewStatusIds.includes(option.value));
  }, [jobsViewStatusIds]);

  React.useEffect(() => {
    if (!jobsViewStatusIds) return;
    const allowedStatuses = new Set(jobsViewStatusIds.map((statusId) => String(statusId)));
    setSelectedStatuses((prev) => prev.filter((status) => allowedStatuses.has(status)));
  }, [jobsViewStatusIds]);
  const canEditJobNotes = React.useMemo(
    () => canEditJobNotesPermission(user),
    [user],
  );
  const canTogglePaid = React.useMemo(
    () => canMarkJobPaid(user),
    [user],
  );

  const handleOpenNotesDialog = React.useCallback((job) => {
    setEditingNotesJobId(job.id);
    setNotesDraft(job.notes ?? '');
    setNotesDialogError('');
    setIsNotesDialogOpen(true);
  }, []);

  const handleCloseNotesDialog = React.useCallback(() => {
    if (notesUpdating) return;
    setIsNotesDialogOpen(false);
    setEditingNotesJobId(null);
    setNotesDraft('');
    setNotesDialogError('');
  }, [notesUpdating]);

  const handleUpdateNotes = React.useCallback(async () => {
    if (!editingNotesJobId) return;

    setNotesUpdating(true);
    setNotesDialogError('');
    const { data, status, message } = await PATCH(`${api.updateJobNotes}/${editingNotesJobId}/notes`, {}, { notes: notesDraft });

    if (status === 200 && data) {
      dispatch(updateJobInStore(data));
      if (selectedJob?.id === editingNotesJobId) {
        setSelectedJob(data);
      }
      setIsNotesDialogOpen(false);
      setEditingNotesJobId(null);
      setNotesDraft('');
      setNotesDialogError('');
      setNotesUpdating(false);
      return;
    }

    setNotesDialogError(message || 'Failed to update notes. Please try again.');
    setNotesUpdating(false);
  }, [editingNotesJobId, notesDraft, dispatch, selectedJob]);

  const performTogglePaid = React.useCallback(async (jobId, newValue) => {
    setLoadingPaid((prev) => ({ ...prev, [jobId]: true }));
    try {
      const response = await PATCH(`${api.updatePaid}/${jobId}/paid`, {}, { value: newValue });

      if (!response.error && response.status === 200 && response.data) {
        dispatch(updateJobInStore(response.data));
        if (selectedJob?.id === jobId) {
          setSelectedJob(response.data);
        }
        return { success: true };
      }

      return { success: false, message: response.message || 'Failed to update paid status.' };
    } catch {
      return { success: false, message: 'Failed to update paid status.' };
    } finally {
      setLoadingPaid((prev) => ({ ...prev, [jobId]: false }));
    }
  }, [dispatch, selectedJob]);

  const handleTogglePaidSingle = React.useCallback(async (jobId, currentPaid) => {
    if (!canTogglePaid) return;
    await performTogglePaid(jobId, !currentPaid);
  }, [canTogglePaid, performTogglePaid]);

  const renderPaidCell = React.useCallback((params) => {
    const isLoading = loadingPaid[params.row.id];
    const isPaid = params.row.paid || false;

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <CircularProgress size={20} />
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%', height: '100%' }}>
        <Switch
          checked={isPaid}
          onChange={() => handleTogglePaidSingle(params.row.id, isPaid)}
          disabled={!canTogglePaid}
          size="small"
          onClick={(event) => event.stopPropagation()}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4caf50',
              '& + .MuiSwitch-track': {
                backgroundColor: '#4caf50',
              },
            },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
              backgroundColor: '#4caf50',
            },
            '& .MuiSwitch-track': {
              backgroundColor: isDarkMode ? '#475569' : '#ccc',
            },
          }}
        />
        <Typography variant="body2" sx={{ color: isPaid ? '#4caf50' : 'text.secondary' }}>
          {isPaid ? 'Yes' : 'No'}
        </Typography>
      </Box>
    );
  }, [loadingPaid, canTogglePaid, handleTogglePaidSingle, isDarkMode]);

  // Wrapper functions for mutual exclusivity between date range and year filters
  const handleDateStartChange = React.useCallback((date) => {
    setSelectedDateStart(date);
    if (date && selectedYear !== 'all') {
      setSelectedYear('all');
    }
  }, [selectedYear]);

  const handleDateEndChange = React.useCallback((date) => {
    setSelectedDateEnd(date);
    if (date && selectedYear !== 'all') {
      setSelectedYear('all');
    }
  }, [selectedYear]);

  const handleYearChange = React.useCallback((year) => {
    setSelectedYear(year);
    if (year !== 'all' && (selectedDateStart || selectedDateEnd)) {
      setSelectedDateStart(null);
      setSelectedDateEnd(null);
    }
  }, [selectedDateStart, selectedDateEnd]);

  const renderNotesCell = React.useCallback((params) => {
    const notes = params.value?.trim();
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, height: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!notes ? (
            <Typography sx={{ color: 'text.secondary' }}>
              -
            </Typography>
          ) : (
            <Tooltip title={notes} arrow placement="top">
              <Typography
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {notes}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {canEditJobNotes && (
          <Tooltip title="Edit notes" arrow placement="top">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenNotesDialog(params.row);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
              aria-label="Edit notes"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canEditJobNotes, handleOpenNotesDialog]);

  const renderTechniciansCell = React.useCallback((params) => {
    const names = getJobTechnicianNames(params.row);
    if (names.length === 0) {
      return (
        <Typography sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', height: '100%' }}>
          —
        </Typography>
      );
    }

    const primaryName = names[0];
    const extraCount = names.length - 1;
    const content = (
      <Typography
        component="span"
        noWrap
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          minWidth: 0,
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {primaryName}
        </Box>
        {extraCount > 0 && (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              ml: 0.5,
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8125rem',
            }}
          >
            +{extraCount}
          </Box>
        )}
      </Typography>
    );

    if (names.length === 1) {
      return content;
    }

    return (
      <Tooltip title={names.join(', ')} arrow placement="top">
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: 0, maxWidth: '100%' }}>
          {content}
        </Box>
      </Tooltip>
    );
  }, []);

  // Define columns inside component to access isDarkMode
  const columns = React.useMemo(() => [
    { 
      field: 'work_order', 
      headerName: 'Work Order', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {params.row?.is_locked && (
            <Tooltip title="Job locked - read only" arrow placement="top">
              <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'unlocked',
      headerName: 'Unlocked',
      width: 100,
      sortable: true,
      valueGetter: (_value, row) => (row.unlocked ? 'Yes' : 'No'),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row?.unlocked ? (
            <Tooltip title="Manually unlocked by owner/GM/accountant" arrow placement="top">
              <LockOpenIcon sx={{ fontSize: 18, color: 'success.main' }} />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 250,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 240,
      renderCell: renderNotesCell,
    },
    { 
      field: 'client_name', 
      headerName: 'Client', 
      width: 150,
      renderCell: (params) => {
        const clientId = params.row.client_id;
        const label = params.value || 'Unknown';
        const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      }
    },
    { 
      field: 'location',
      headerName: 'Location', 
      width: 200,
      valueGetter: (value, row) => {
        const parts = [];
        if (row.city) parts.push(row.city);
        if (row.state) parts.push(row.state);
        return parts.join(', ');
      }
    },
    { 
      field: 'address',
      headerName: 'Address', 
      width: 250,
    },
    {
      field: 'status', 
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const statusId = Number(params.value);
        const label = getStatusLabel(statusId);
        const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);

        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      }
    },
    {
      field: 'priority', 
      headerName: 'Priority',
      width: 110,
      renderCell: (params) => {
        const priority = params.value;
        const label = getPriorityLabel(priority);
        let bgColor = '';
        let textColor = '';
        let borderColor = '';
        if (priority == '1') {
          bgColor = isDarkMode ? '#7f1d1d' : '#fee2e2';
          textColor = isDarkMode ? '#fca5a5' : '#991b1b';
          borderColor = '#ef4444';
        } else if (priority == '2') {
          bgColor = isDarkMode ? '#78350f' : '#fef3c7';
          textColor = isDarkMode ? '#fbbf24' : '#92400e';
          borderColor = '#f59e0b';
        } else if (priority == '3') {
          bgColor = isDarkMode ? '#1e3a8a' : '#dbeafe';
          textColor = isDarkMode ? '#93c5fd' : '#1e3a8a';
          borderColor = '#3b82f6';
        } else if (priority == '4') {
          bgColor = isDarkMode ? '#374151' : '#e5e7eb';
          textColor = isDarkMode ? '#d1d5db' : '#374151';
          borderColor = '#6b7280';
        } else {
          bgColor = isDarkMode ? '#374151' : '#e5e7eb';
          textColor = isDarkMode ? '#d1d5db' : '#374151';
          borderColor = '#6b7280';
        }
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      }
    },
    {
      field: 'sla',
      headerName: 'SLA',
      width: 165,
      valueFormatter: (value) => formatSlaTableDisplay(value),
    },
    { 
      field: 'trade',
      headerName: 'Trade', 
      width: 120,
    },
    { 
      field: 'store_name',
      headerName: 'Store', 
      width: 140,
    },
    { 
      field: 'admin',
      headerName: 'Admin', 
      width: 150,
      valueGetter: (_value, row) => displayUserByEmail(row.admin),
    },
    { 
      field: 'dispatcher',
      headerName: 'Dispatcher', 
      width: 150,
      valueGetter: (_value, row) => displayUserByEmail(row.dispatcher),
    },
    {
      field: 'technicians',
      headerName: 'Technicians',
      width: 180,
      sortable: false,
      valueGetter: (_value, row) => getJobTechnicianNames(row).join(', '),
      renderCell: renderTechniciansCell,
    },
    { 
      field: 'created_at',
      headerName: 'Start Date', 
      width: 130,
      valueFormatter: (value) => value ? value.slice(0, 10) : '',
    },
    { 
      field: 'nte',
      headerName: 'NTE', 
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('nte'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const formattedValue = `$${numValue.toLocaleString()}`;
        const isPositive = numValue > 0;
        return (
          <Typography sx={{ 
            color: isPositive ? '#22c55e' : 'text.primary', 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}>
            {formattedValue}
          </Typography>
        );
      }
    },
    {
      field: 'paid',
      headerName: 'Paid',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => (row.paid ? 'Yes' : 'No'),
      renderCell: renderPaidCell,
    },
  ], [isDarkMode, renderNotesCell, renderTechniciansCell, renderPaidCell, displayUserByEmail]);

  // AR-AP columns
  const arApColumns = React.useMemo(() => [
    { 
      field: 'client_name', 
      headerName: 'Client', 
      width: 150,
      renderCell: (params) => {
        const clientId = params.row.client_id;
        const label = params.value || 'Unknown';
        const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      }
    },
    { 
      field: 'work_order', 
      headerName: 'Work Order', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {params.row?.is_locked && (
            <Tooltip title="Job locked - read only" arrow placement="top">
              <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'unlocked',
      headerName: 'Unlocked',
      width: 100,
      sortable: true,
      valueGetter: (_value, row) => (row.unlocked ? 'Yes' : 'No'),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row?.unlocked ? (
            <Tooltip title="Manually unlocked by owner/GM/accountant" arrow placement="top">
              <LockOpenIcon sx={{ fontSize: 18, color: 'success.main' }} />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 240,
      renderCell: renderNotesCell,
    },
    {
      field: 'technicians',
      headerName: 'Technicians',
      width: 180,
      sortable: false,
      valueGetter: (_value, row) => getJobTechnicianNames(row).join(', '),
      renderCell: renderTechniciansCell,
    },
    { 
      field: 'created_at',
      headerName: 'Create Date', 
      width: 130,
      valueFormatter: (value) => value ? value.slice(0, 10) : '',
    },
    { 
      field: 'invoiced_date',
      headerName: 'Invoiced Date', 
      width: 130,
      valueFormatter: (value) => value ? value.slice(0, 10) : '—',
    },
    { 
      field: 'nte',
      headerName: 'NTE', 
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('nte'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const formattedValue = `$${numValue.toLocaleString()}`;
        return (
          <Typography sx={{ 
            color: '#22c55e', 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}>
            {formattedValue}
          </Typography>
        );
      }
    },
    { 
      field: 'total_cost',
      headerName: 'Total Cost', 
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('total_cost'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const formattedValue = `$${numValue.toLocaleString()}`;
        return (
          <Typography sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}>
            {formattedValue}
          </Typography>
        );
      }
    },
    { 
      field: 'technicians_cost',
      headerName: 'Technicians Cost', 
      width: 150,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('technicians_cost'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const formattedValue = `$${numValue.toLocaleString()}`;
        return (
          <Typography sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}>
            {formattedValue}
          </Typography>
        );
      }
    },
    { 
      field: 'parts_cost',
      headerName: 'Parts Cost', 
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('parts_cost'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const formattedValue = `$${numValue.toLocaleString()}`;
        return (
          <Typography sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}>
            {formattedValue}
          </Typography>
        );
      }
    },
    { 
      field: 'profit',
      headerName: 'Profit', 
      width: 130,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: numericFieldValueGetter('profit'),
      renderCell: (params) => {
        const numValue = params.value ?? 0;
        const formattedValue = `$${numValue.toLocaleString()}`;
        const isPositive = numValue >= 0;
        return (
          <Typography sx={{ 
            color: isPositive ? '#22c55e' : '#ef4444',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}>
            {formattedValue}
          </Typography>
        );
      }
    },
    { 
      field: 'invoice_number',
      headerName: 'Invoice #', 
      width: 130,
      valueFormatter: (value) => value || '—',
    },
    {
      field: 'paid',
      headerName: 'Paid',
      width: 120,
      sortable: true,
      valueGetter: (_value, row) => (row.paid ? 'Yes' : 'No'),
      renderCell: renderPaidCell,
    },
    // statusColorsRevision busts column defs when status colors change in Settings
  ], [renderNotesCell, renderTechniciansCell, renderPaidCell, statusColorsRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const descriptionFields = React.useMemo(() => columns.map((column) => column.field), [columns]);
  const arApFields = React.useMemo(() => arApColumns.map((column) => column.field), [arApColumns]);

  const sanitizedGridPreferences = React.useMemo(() => ({
    description: sanitizeGridPreference(gridPreferences.description ?? DEFAULT_GRID_PREFERENCE, descriptionFields),
    'ar-ap': sanitizeGridPreference(gridPreferences['ar-ap'] ?? DEFAULT_GRID_PREFERENCE, arApFields),
  }), [gridPreferences, descriptionFields, arApFields]);

  const activeColumns = viewMode === 'ar-ap' ? arApColumns : columns;
  const activeFields = viewMode === 'ar-ap' ? arApFields : descriptionFields;
  const activeGridPreference = sanitizedGridPreferences[viewMode];
  const columnsByField = React.useMemo(
    () => new Map(activeColumns.map((column) => [column.field, column])),
    [activeColumns],
  );
  const columnsPanelOpen = Boolean(columnsPanelAnchorEl);

  const orderedColumns = React.useMemo(
    () => applyColumnOrder(activeColumns, activeGridPreference.columnOrder),
    [activeColumns, activeGridPreference.columnOrder],
  );

  const displayColumns = React.useMemo(
    () => (isMobile ? scaleColumnsForMobile(orderedColumns) : orderedColumns),
    [isMobile, orderedColumns],
  );

  React.useEffect(() => {
    window.localStorage.setItem(GRID_PREFERENCES_STORAGE_KEY, JSON.stringify(sanitizedGridPreferences));
  }, [sanitizedGridPreferences]);

  const jobsQuickNavTabs = React.useMemo(() => ([
    ...JOBS_QUICK_NAV_TABS,
    ...(canViewDeleted ? [{ viewKey: 'deleted', label: 'Deleted', path: routes.jobsDeleted }] : []),
  ]), [canViewDeleted]);

  React.useEffect(() => {
    if (isDeletedView && !canViewDeleted) {
      navigate(routes.jobsAll, { replace: true });
    }
  }, [isDeletedView, canViewDeleted, navigate]);

  const jobsTabCounts = React.useMemo(
    () => ({
      all: jobs.length,
      active: jobs.filter((job) => ACTIVE_JOB_STATUS_IDS.includes(Number(job.status))).length,
      archived: jobs.filter((job) => ARCHIVED_JOB_STATUS_IDS.includes(Number(job.status))).length,
      jobDone: jobs.filter((job) => JOB_DONE_VIEW_STATUS_IDS.includes(Number(job.status))).length,
      priority: jobs.filter((job) => PRIORITY_JOB_STATUS_IDS.includes(Number(job.status))).length,
      deleted: deletedJobs.length,
    }),
    [jobs, deletedJobs],
  );

  // Generate year options (current year and past 5 years)
  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push((currentYear - i).toString());
    }
    return ['all', ...years];
  }, []);

  // Get available states - use 2-letter codes (keys) from constants, sorted by state name
  const availableStates = React.useMemo(() => {
    return Object.keys(states).sort((a, b) => states[a].localeCompare(states[b]));
  }, []);

  // Get unique trades from jobs
  const availableTrades = React.useMemo(() => {
    const trades = new Set();
    jobs.forEach(job => {
      if (job.trade) {
        trades.add(job.trade);
      }
    });
    return Array.from(trades).sort();
  }, [jobs]);

  // Client filter options: dispatchers don't have clients in Redux, so derive from jobs
  const clientFilterOptions = React.useMemo(() => {
    if (isOnlyDispatcher(user)) {
      const seen = new Set();
      return (jobs || [])
        .filter(job => {
          if (!job.client_id || seen.has(job.client_id)) return false;
          seen.add(job.client_id);
          return true;
        })
        .map(job => ({ id: job.client_id, client_name: job.client_name || 'Unknown' }))
        .sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
    }
    return clientsData.clients || [];
  }, [user, jobs, clientsData.clients]);

  const currentViewBucket = React.useMemo(
    () => getViewBucket(savedSearchesStorage, jobsView),
    [savedSearchesStorage, jobsView],
  );

  const getCurrentFilterSnapshot = React.useCallback(() => captureFilterSnapshot({
    searchQuery,
    selectedStatuses,
    selectedPriorities,
    selectedStates,
    selectedYear,
    selectedTrades,
    selectedTags,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    unlockedOnly,
  }), [
    searchQuery,
    selectedStatuses,
    selectedPriorities,
    selectedStates,
    selectedYear,
    selectedTrades,
    selectedTags,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    unlockedOnly,
  ]);

  // Strict client sanitize only for primary-role admins once clients have loaded successfully.
  const sanitizeClientOptions = isPrimaryAdmin && clientsReady ? clientFilterOptions : undefined;

  const applyFilterSnapshot = React.useCallback((snapshot) => {
    const sanitized = sanitizeFilterSnapshot(snapshot, {
      jobsViewStatusIds,
      tagOptions: tagFilterOptions,
      clientOptions: sanitizeClientOptions,
    });

    suppressCustomDetectionRef.current = true;
    setSearchQuery(sanitized.searchQuery);
    setSelectedStatuses(sanitized.selectedStatuses);
    setSelectedPriorities(sanitized.selectedPriorities);
    setSelectedStates(sanitized.selectedStates);
    setSelectedYear(sanitized.selectedYear);
    setSelectedTrades(sanitized.selectedTrades);
    setSelectedTags(sanitized.selectedTags);
    setSelectedClients(sanitized.selectedClients);
    setSelectedDateStart(parseDateField(sanitized.selectedDateStart));
    setSelectedDateEnd(parseDateField(sanitized.selectedDateEnd));
    setUnlockedOnly(sanitized.unlockedOnly === true);
  }, [jobsViewStatusIds, tagFilterOptions, sanitizeClientOptions]);

  const handleResetFilters = React.useCallback(() => {
    applyFilterSnapshot(EMPTY_FILTER_SNAPSHOT);
    setActiveSavedSearchId(null);
  }, [applyFilterSnapshot]);

  const handleApplySavedSearch = React.useCallback((searchId) => {
    const search = findSearchById(savedSearchesStorage, jobsView, searchId);
    if (!search) return;
    applyFilterSnapshot(search.filters);
    setActiveSavedSearchId(search.id);
  }, [savedSearchesStorage, jobsView, applyFilterSnapshot]);

  const handleSaveSearch = React.useCallback(({ name, setAsDefault }) => {
    if (!companyId) return;
    const { storage: nextStorage, searchId } = upsertSearch(companyId, savedSearchesStorage, jobsView, {
      name,
      filters: getCurrentFilterSnapshot(),
      setAsDefault,
    });
    setSavedSearchesStorage(nextStorage);
    setActiveSavedSearchId(searchId);
  }, [companyId, savedSearchesStorage, jobsView, getCurrentFilterSnapshot]);

  const handleDeleteSavedSearch = React.useCallback((searchId) => {
    if (!companyId) return;
    const nextStorage = deleteSearch(companyId, savedSearchesStorage, jobsView, searchId);
    setSavedSearchesStorage(nextStorage);
    if (activeSavedSearchId === searchId) {
      setActiveSavedSearchId(null);
    }
  }, [companyId, savedSearchesStorage, jobsView, activeSavedSearchId]);

  const handleRenameSavedSearch = React.useCallback((searchId, name) => {
    if (!companyId) return;
    const nextStorage = renameSearch(companyId, savedSearchesStorage, jobsView, searchId, name);
    setSavedSearchesStorage(nextStorage);
  }, [companyId, savedSearchesStorage, jobsView]);

  const handleSetDefaultSavedSearch = React.useCallback((searchId) => {
    if (!companyId) return;
    const nextStorage = setDefaultSearch(companyId, savedSearchesStorage, jobsView, searchId);
    setSavedSearchesStorage(nextStorage);
  }, [companyId, savedSearchesStorage, jobsView]);

  const handleClearDefaultSavedSearch = React.useCallback(() => {
    if (!companyId) return;
    const nextStorage = clearDefaultSearch(companyId, savedSearchesStorage, jobsView);
    setSavedSearchesStorage(nextStorage);
  }, [companyId, savedSearchesStorage, jobsView]);

  // Reload company-scoped saved searches when the active company changes.
  React.useEffect(() => {
    if (loadedCompanyIdRef.current === companyId) return;
    loadedCompanyIdRef.current = companyId;
    const storage = loadJobsSavedSearches(companyId);
    setSavedSearchesStorage(storage);
    if (isDeletedView) {
      setActiveSavedSearchId(null);
      return;
    }
    const defaultSearch = getDefaultSearchForView(storage, jobsView);
    applyFilterSnapshot(defaultSearch ? defaultSearch.filters : EMPTY_FILTER_SNAPSHOT);
    setActiveSavedSearchId(defaultSearch ? defaultSearch.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Primary-role admin: drop inaccessible clients from this company's saved searches.
  React.useEffect(() => {
    if (!isPrimaryAdmin || !clientsReady || !companyId) return;
    if (loadedCompanyIdRef.current !== companyId) return;

    const pruned = pruneSavedSearchesAgainstClients(
      companyId,
      loadJobsSavedSearches(companyId),
      clientFilterOptions,
    );
    setSavedSearchesStorage(pruned.storage);

    suppressCustomDetectionRef.current = true;
    setSelectedClients((prev) => {
      const next = sanitizeFilterSnapshot(
        { selectedClients: prev },
        { clientOptions: clientFilterOptions },
      ).selectedClients;
      if (arraysEqualById(prev, next)) return prev;
      return next;
    });
  }, [isPrimaryAdmin, clientsReady, clientFilterOptions, companyId]);

  React.useEffect(() => {
    if (isDeletedView) return;
    const storage = loadJobsSavedSearches(companyId);
    setSavedSearchesStorage(storage);
    const defaultSearch = getDefaultSearchForView(storage, jobsView);
    const snapshot = defaultSearch ? defaultSearch.filters : EMPTY_FILTER_SNAPSHOT;
    const sanitized = sanitizeFilterSnapshot(snapshot, {
      jobsViewStatusIds,
      tagOptions: tagFilterOptions,
      clientOptions: sanitizeClientOptions,
    });

    suppressCustomDetectionRef.current = true;
    setSearchQuery(sanitized.searchQuery);
    setSelectedStatuses(sanitized.selectedStatuses);
    setSelectedPriorities(sanitized.selectedPriorities);
    setSelectedStates(sanitized.selectedStates);
    setSelectedYear(sanitized.selectedYear);
    setSelectedTrades(sanitized.selectedTrades);
    setSelectedTags(sanitized.selectedTags);
    setSelectedClients(sanitized.selectedClients);
    setSelectedDateStart(parseDateField(sanitized.selectedDateStart));
    setSelectedDateEnd(parseDateField(sanitized.selectedDateEnd));
    setUnlockedOnly(sanitized.unlockedOnly === true);
    setActiveSavedSearchId(defaultSearch ? defaultSearch.id : null);
    // Only re-apply when the jobs route view changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsView]);

  React.useEffect(() => {
    if (suppressCustomDetectionRef.current) {
      suppressCustomDetectionRef.current = false;
      return;
    }
    if (!activeSavedSearchId) return;

    const activeSearch = findSearchById(savedSearchesStorage, jobsView, activeSavedSearchId);
    if (!activeSearch) {
      setActiveSavedSearchId(null);
      return;
    }

    const sanitizedCurrent = sanitizeFilterSnapshot(getCurrentFilterSnapshot(), {
      jobsViewStatusIds,
      tagOptions: tagFilterOptions,
      clientOptions: sanitizeClientOptions,
    });

    if (!snapshotsMatch(sanitizedCurrent, activeSearch.filters)) {
      setActiveSavedSearchId(null);
    }
  }, [
    searchQuery,
    selectedStatuses,
    selectedPriorities,
    selectedStates,
    selectedYear,
    selectedTrades,
    selectedTags,
    selectedClients,
    selectedDateStart,
    selectedDateEnd,
    unlockedOnly,
    activeSavedSearchId,
    savedSearchesStorage,
    jobsView,
    jobsViewStatusIds,
    tagFilterOptions,
    sanitizeClientOptions,
    getCurrentFilterSnapshot,
  ]);

  // Handle status filter toggle
  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Handle priority filter toggle
  const handlePriorityToggle = (priority) => {
    setSelectedPriorities(prev => {
      if (prev.includes(priority)) {
        return prev.filter(p => p !== priority);
      } else {
        return [...prev, priority];
      }
    });
  };

  React.useEffect(() => {
    const key = isMobileRef.current ? TOP_FILTERS_MOBILE_STORAGE_KEY : TOP_FILTERS_STORAGE_KEY;
    window.localStorage.setItem(key, String(topFiltersExpanded));
  }, [topFiltersExpanded]);

  React.useEffect(() => {
    const key = isMobileRef.current
      ? SECONDARY_FILTERS_MOBILE_STORAGE_KEY
      : SECONDARY_FILTERS_STORAGE_KEY;
    window.localStorage.setItem(key, String(statusFilterExpanded));
  }, [statusFilterExpanded]);

  React.useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // When crossing the mobile/desktop breakpoint, load that viewport's saved preference.
  React.useEffect(() => {
    if (skipViewportFilterSyncRef.current) {
      skipViewportFilterSyncRef.current = false;
      return;
    }
    setTopFiltersExpanded(
      readFiltersExpandedPreference(
        TOP_FILTERS_STORAGE_KEY,
        TOP_FILTERS_MOBILE_STORAGE_KEY,
        isMobile,
      ),
    );
    setStatusFilterExpanded(
      readFiltersExpandedPreference(
        SECONDARY_FILTERS_STORAGE_KEY,
        SECONDARY_FILTERS_MOBILE_STORAGE_KEY,
        isMobile,
      ),
    );
  }, [isMobile]);

  React.useEffect(() => {
    let cancelled = false;
    fetchJobTags({ activeOnly: true }).then((tags) => {
      if (!cancelled) setTagFilterOptions(tags);
    });
    return () => { cancelled = true; };
  }, []);

  const jobMatchesSelectedTags = React.useCallback((job) => {
    if (selectedTags.length === 0) return true;
    const selectedTagIds = new Set(selectedTags.map((tag) => tag.id));
    return Array.isArray(job.tags) && job.tags.some((tag) => selectedTagIds.has(tag.id));
  }, [selectedTags]);

  const areFilterSectionsCollapsed = !topFiltersExpanded && !statusFilterExpanded;

  // Fetch jobs
  // useEffect(() => {
  //   const fetchJobs = async () => {
  //     const {data, status} = await GET(api.getJobs);
  //     if (status === 200) dispatch(setJobs({jobs: data.jobs}));
  //     else dispatch(setJobs({error: 'Something went wrong, please try again later.'}));
  //   };
    
  //   fetchJobs();
  // }, []);

  // Handle row click to open job details
  const handleRowClick = React.useCallback((params) => {
    setInitialJobDetailsTabValue(0);
    setSelectedJob(params.row);
  }, []);

  const handleDeletedRowClick = React.useCallback(async (params) => {
    setDeletedJobDetailError(null);
    const { data, status, message } = await GET(`${api.getDeletedJobById}/${params.row.id}`);
    if (status === 200 && data) {
      setSelectedDeletedJob(data);
      return;
    }
    setDeletedJobDetailError(message || 'Failed to load deleted work order details.');
  }, []);

  React.useEffect(() => {
    if (!isDeletedView) return undefined;

    let cancelled = false;
    const fetchDeletedJobs = async () => {
      setDeletedJobsLoading(true);
      setDeletedJobsError(null);
      const { data, status, message } = await GET(api.getDeletedJobs);
      if (cancelled) return;
      if (status === 200) {
        setDeletedJobs(data?.deletedJobs || []);
      } else {
        setDeletedJobs([]);
        setDeletedJobsError(message || 'Failed to load deleted work orders.');
      }
      setDeletedJobsLoading(false);
    };

    fetchDeletedJobs();
    return () => {
      cancelled = true;
    };
  }, [isDeletedView]);

  const deletedColumns = React.useMemo(() => ([
    {
      field: 'work_order',
      headerName: 'Work Order',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.work_order || '—',
    },
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const statusId = Number(params.row.status);
        const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);
        return (
          <Chip
            label={getStatusLabel(statusId)}
            size="small"
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              border: `1px solid ${borderColor}`,
            }}
          />
        );
      },
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1.5,
      minWidth: 180,
      valueGetter: (_value, row) => [row.address, row.city, row.state].filter(Boolean).join(', '),
    },
    {
      field: 'deleted_at',
      headerName: 'Deleted At',
      width: 170,
      valueGetter: (_value, row) => formatShortDateTime(row.deleted_at),
    },
    {
      field: 'deleted_by_email',
      headerName: 'Deleted By',
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => row.deleted_by_email || '—',
    },
  ]), []);

  const filteredDeletedJobs = React.useMemo(() => {
    if (!deletedSearchQuery.trim()) return deletedJobs;
    const queryText = deletedSearchQuery.trim().toLowerCase();
    return deletedJobs.filter((row) => {
      const haystack = [
        row.work_order,
        row.client_name,
        row.description,
        row.invoice_numbers,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(queryText);
    });
  }, [deletedJobs, deletedSearchQuery]);

  // Open JobDetailsDialog from in-app navigation state or email deep links (?openJobId=)
  React.useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search);
    const openJobId = location?.state?.openJobId || params.get('openJobId');
    if (!openJobId) return undefined;

    const tab = location?.state?.openJobTab || params.get('openJobTab');
    const tabIndex =
      tab === 'technicians' ? 1
        : tab === 'parts' ? 2
          : tab === 'proposals' ? 4
            : tab === 'comments' ? 6
              : 0;

    const clearOpenParams = () => {
      const next = new URLSearchParams(location.search);
      next.delete('openJobId');
      next.delete('openJobTab');
      const search = next.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true, state: null },
      );
    };

    const openJob = (job) => {
      if (cancelled || !job) return;
      setInitialJobDetailsTabValue(tabIndex);
      setSelectedJob(job);
      clearOpenParams();
    };

    const existing = jobs?.find((j) => j.id === openJobId);
    if (existing) {
      openJob(existing);
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const { status, data } = await GET(`${api.getJobById}/${openJobId}`);
        if (cancelled) return;
        if (status === 200 && data) {
          openJob(data);
          return;
        }
      } catch {
        // ignore — fall through to clear stale deep-link params
      }
      if (!cancelled) clearOpenParams();
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state, location.search, jobs, location.pathname]);

  // Fetch AR-AP data when user switches to AR-AP view; merge into Redux jobs
  React.useEffect(() => {
    const fetchArApJobs = async () => {
      if (viewMode !== 'ar-ap') return;

      setArApLoading(true);
      setArApError(null);

      try {
        const { data, status } = await GET(api.getJobsArAp);
        if (status === 200) {
          dispatch(mergeJobsArAp({ jobs: data?.jobs ?? [] }));
        } else {
          setArApError('Failed to load AR-AP data. Please try again.');
        }
      } catch {
        setArApError('Failed to load AR-AP data. Please try again.');
      } finally {
        setArApLoading(false);
      }
    };

    fetchArApJobs();
  }, [viewMode, dispatch]);

  const updateActiveGridPreference = React.useCallback((updater) => {
    setGridPreferences((prev) => {
      const currentPreference = sanitizeGridPreference(prev?.[viewMode] ?? DEFAULT_GRID_PREFERENCE, activeFields);
      const nextPreference = typeof updater === 'function' ? updater(currentPreference) : updater;
      return {
        ...prev,
        [viewMode]: sanitizeGridPreference(nextPreference, activeFields),
      };
    });
  }, [viewMode, activeFields]);

  // Handle columns panel toggle
  const handleColumnsPanelToggle = (event) => {
    setColumnsPanelAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleColumnsPanelClose = () => {
    setColumnsPanelAnchorEl(null);
  };

  const handleColumnVisibilityModelChange = React.useCallback((model) => {
    updateActiveGridPreference((currentPreference) => ({
      ...currentPreference,
      columnVisibilityModel: model,
    }));
  }, [updateActiveGridPreference]);

  const handleColumnVisibilityToggle = React.useCallback((field) => {
    updateActiveGridPreference((currentPreference) => {
      const isVisible = currentPreference.columnVisibilityModel[field] !== false;
      return {
        ...currentPreference,
        columnVisibilityModel: {
          ...currentPreference.columnVisibilityModel,
          [field]: !isVisible,
        },
      };
    });
  }, [updateActiveGridPreference]);

  const handleReorderColumns = React.useCallback((activeField, overField) => {
    updateActiveGridPreference((currentPreference) => {
      const nextOrder = [...currentPreference.columnOrder];
      const fromIndex = nextOrder.indexOf(activeField);
      const toIndex = nextOrder.indexOf(overField);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return currentPreference;

      const [movedField] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, movedField);
      return {
        ...currentPreference,
        columnOrder: nextOrder,
      };
    });
  }, [updateActiveGridPreference]);

  const handleResetColumns = React.useCallback(() => {
    updateActiveGridPreference({
      columnOrder: activeFields,
      columnVisibilityModel: {},
    });
  }, [updateActiveGridPreference, activeFields]);

  // Jobs matching current view and filters except status (for per-status chip counts)
  const jobsForStatusCounts = React.useMemo(() => {
    let filtered = jobs;

    if (jobsViewStatusIds) {
      filtered = filtered.filter((job) => jobsViewStatusIds.includes(Number(job.status)));
    }

    if (selectedPriorities.length > 0) {
      filtered = filtered.filter(job => selectedPriorities.includes(String(job.priority)));
    }

    if (selectedStates.length > 0) {
      filtered = filtered.filter(job => selectedStates.includes(job.state));
    }

    if (selectedYear !== 'all') {
      filtered = filtered.filter(job => {
        if (!job.created_at) return false;
        const jobYear = new Date(job.created_at).getFullYear().toString();
        return jobYear === selectedYear;
      });
    }

    if (selectedTrades.length > 0) {
      filtered = filtered.filter(job => selectedTrades.includes(job.trade));
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(jobMatchesSelectedTags);
    }

    if (selectedClients.length > 0) {
      const selectedClientIds = selectedClients.map(c => c.id);
      filtered = filtered.filter(job =>
        (job.client_id && selectedClientIds.includes(job.client_id)) ||
        (job.client_name && selectedClients.some(c => (c.client_name || '').toLowerCase() === (job.client_name || '').toLowerCase()))
      );
    }

    if (selectedDateStart || selectedDateEnd) {
      filtered = filtered.filter(job => {
        if (!job.created_at) return false;
        const jobDate = new Date(job.created_at);

        if (selectedDateStart && selectedDateEnd) {
          const start = new Date(selectedDateStart);
          const end = new Date(selectedDateEnd);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          return jobDate >= start && jobDate <= end;
        } else if (selectedDateStart) {
          const start = new Date(selectedDateStart);
          start.setHours(0, 0, 0, 0);
          return jobDate >= start;
        } else if (selectedDateEnd) {
          const end = new Date(selectedDateEnd);
          end.setHours(23, 59, 59, 999);
          return jobDate <= end;
        }
        return true;
      });
    }

    if (unlockedOnly) {
      filtered = filtered.filter(job => job.unlocked === true);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job =>
        job.description?.toLowerCase().includes(query) ||
        job.client_name?.toLowerCase().includes(query) ||
        job.work_order?.toLowerCase().includes(query) ||
        job.invoice_number?.toLowerCase().includes(query) ||
        job.trade?.toLowerCase().includes(query) ||
        job.store_name?.toLowerCase().includes(query) ||
        job.city?.toLowerCase().includes(query) ||
        job.state?.toLowerCase().includes(query) ||
        displayUserByEmail(job.admin).toLowerCase().includes(query) ||
        job.admin?.toLowerCase().includes(query) ||
        displayUserByEmail(job.dispatcher).toLowerCase().includes(query) ||
        job.dispatcher?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [jobs, jobsViewStatusIds, searchQuery, selectedPriorities, selectedStates, selectedYear, selectedTrades, selectedTags, jobMatchesSelectedTags, selectedClients, selectedDateStart, selectedDateEnd, unlockedOnly, displayUserByEmail]);

  const statusCountsByStatusId = React.useMemo(() => {
    const counts = {};
    jobsForStatusCounts.forEach((job) => {
      const statusId = String(job.status);
      counts[statusId] = (counts[statusId] || 0) + 1;
    });
    return counts;
  }, [jobsForStatusCounts]);

  // Filter jobs based on all filters and search query
  const filteredJobs = React.useMemo(() => {
    let filtered = jobs;

    // Apply route-level jobs tab view first
    if (jobsViewStatusIds) {
      filtered = filtered.filter((job) => jobsViewStatusIds.includes(Number(job.status)));
    }

    // Apply status filter (convert to string for comparison since DB values may be integers)
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(job => selectedStatuses.includes(String(job.status)));
    }
    
    // Apply priority filter (convert to string for comparison since DB values may be integers)
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter(job => selectedPriorities.includes(String(job.priority)));
    }
    
    // Apply state filter
    if (selectedStates.length > 0) {
      filtered = filtered.filter(job => selectedStates.includes(job.state));
    }
    
    // Apply year filter
    if (selectedYear !== 'all') {
      filtered = filtered.filter(job => {
        if (!job.created_at) return false;
        const jobYear = new Date(job.created_at).getFullYear().toString();
        return jobYear === selectedYear;
      });
    }
    
    // Apply trade filter
    if (selectedTrades.length > 0) {
      filtered = filtered.filter(job => selectedTrades.includes(job.trade));
    }

    // Apply tags filter (job must have at least one selected tag)
    if (selectedTags.length > 0) {
      filtered = filtered.filter(jobMatchesSelectedTags);
    }
    
    // Apply client filter (by client_id; fallback to client_name if client_id missing)
    if (selectedClients.length > 0) {
      const selectedClientIds = selectedClients.map(c => c.id);
      filtered = filtered.filter(job =>
        (job.client_id && selectedClientIds.includes(job.client_id)) ||
        (job.client_name && selectedClients.some(c => (c.client_name || '').toLowerCase() === (job.client_name || '').toLowerCase()))
      );
    }
    
    // Apply date range filter
    if (selectedDateStart || selectedDateEnd) {
      filtered = filtered.filter(job => {
        if (!job.created_at) return false;
        const jobDate = new Date(job.created_at);
        
        if (selectedDateStart && selectedDateEnd) {
          // Both dates selected - filter for jobs within range (inclusive)
          const start = new Date(selectedDateStart);
          const end = new Date(selectedDateEnd);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          return jobDate >= start && jobDate <= end;
        } else if (selectedDateStart) {
          // Only start date - filter for jobs on or after start date
          const start = new Date(selectedDateStart);
          start.setHours(0, 0, 0, 0);
          return jobDate >= start;
        } else if (selectedDateEnd) {
          // Only end date - filter for jobs on or before end date
          const end = new Date(selectedDateEnd);
          end.setHours(23, 59, 59, 999);
          return jobDate <= end;
        }
        return true;
      });
    }
    
    if (unlockedOnly) {
      filtered = filtered.filter(job => job.unlocked === true);
    }
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job => 
        job.description?.toLowerCase().includes(query) ||
        job.client_name?.toLowerCase().includes(query) ||
        job.work_order?.toLowerCase().includes(query) ||
        job.invoice_number?.toLowerCase().includes(query) ||
        job.trade?.toLowerCase().includes(query) ||
        job.store_name?.toLowerCase().includes(query) ||
        job.city?.toLowerCase().includes(query) ||
        job.state?.toLowerCase().includes(query) ||
        displayUserByEmail(job.admin).toLowerCase().includes(query) ||
        job.admin?.toLowerCase().includes(query) ||
        displayUserByEmail(job.dispatcher).toLowerCase().includes(query) ||
        job.dispatcher?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [jobs, jobsViewStatusIds, searchQuery, selectedStatuses, selectedPriorities, selectedStates, selectedYear, selectedTrades, selectedTags, jobMatchesSelectedTags, selectedClients, selectedDateStart, selectedDateEnd, unlockedOnly, displayUserByEmail]);

  // Error fetching jobs (deleted view uses its own API)
  if (error && !isDeletedView) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>⚠️</span>
        {error}
      </Box>
    );
  }

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ 
          height: '100vh', 
          width: '100%', 
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with title, subtitle and Add Job button */}
        <Box sx={{ mb: topFiltersExpanded ? 2 : 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography 
                variant="h4" 
                component="h1"
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', md: '2rem' },
                  color: 'text.primary',
                  mb: 0.5,
                }}
              >
                {isDeletedView ? 'Deleted Work Orders' : 'Work Orders'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                {isDeletedView
                  ? 'Browse work orders that were removed from the active list'
                  : 'Manage and track all your work orders'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
              {!isDeletedView && (
              <JobsSavedSearchesBar
                variant="header"
                jobsView={jobsView}
                savedSearches={currentViewBucket.searches}
                defaultSearchId={currentViewBucket.defaultId}
                activeSearchId={activeSavedSearchId}
                onApplySearch={handleApplySavedSearch}
                onSaveSearch={handleSaveSearch}
                onDeleteSearch={handleDeleteSavedSearch}
                onRenameSearch={handleRenameSavedSearch}
                onSetDefault={handleSetDefaultSavedSearch}
                onClearDefault={handleClearDefaultSavedSearch}
                onResetFilters={handleResetFilters}
                isDarkMode={isDarkMode}
              />
              )}
              {!isDeletedView && canCreateOrEditJob(user) && (
                <Button
                  variant="contained"
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                    color: isDarkMode ? '#000' : '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: { xs: 1.5, md: 3 },
                    py: 1.25,
                    borderRadius: 1.5,
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      boxShadow: 'none',
                    }
                  }}
                >
                  {isMobile ? '+ Add' : '+ Add Work Order'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {isDeletedView ? (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by work order, client, invoice #, or description..."
              value={deletedSearchQuery}
              onChange={(e) => setDeletedSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  backgroundColor: 'background.paper',
                  fontSize: '0.875rem',
                  height: '42px',
                  '& input::placeholder': {
                    color: 'text.secondary',
                    opacity: 1,
                  },
                  '& fieldset': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                    borderWidth: '1px',
                  },
                },
              }}
            />
          </Box>
        ) : (
          <>
        {areFilterSectionsCollapsed && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              mb: 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                onClick={() => setTopFiltersExpanded(true)}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 0.5,
                  py: 0,
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}
                endIcon={<ExpandMoreIcon fontSize="small" />}
              >
                Quick Filters
              </Button>
              <Button
                onClick={() => setStatusFilterExpanded(true)}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 0.5,
                  py: 0,
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}
                endIcon={<ExpandMoreIcon fontSize="small" />}
              >
                Status & Filters
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isMobile && (
                <IconButton
                  onClick={handleColumnsPanelToggle}
                  aria-label="Manage columns"
                  size="small"
                  sx={{
                    border: '1px solid',
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <ViewColumnIcon fontSize="small" />
                </IconButton>
              )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(event, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
              sx={{
                height: '36px',
                flexShrink: 0,
                display: isDeletedView ? 'none' : 'inline-flex',
              }}
            >
              <ToggleButton
                value="description"
                sx={{
                  px: 2,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: viewMode === 'description' ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                  backgroundColor: viewMode === 'description' ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  '&:hover': {
                    backgroundColor: viewMode === 'description'
                      ? (isDarkMode ? '#e0e0e0' : '#333')
                      : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                    color: isDarkMode ? '#000' : '#fff',
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                    },
                  },
                }}
              >
                Description
              </ToggleButton>
              <ToggleButton
                value="ar-ap"
                sx={{
                  px: 2,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: viewMode === 'ar-ap' ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                  backgroundColor: viewMode === 'ar-ap' ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  '&:hover': {
                    backgroundColor: viewMode === 'ar-ap'
                      ? (isDarkMode ? '#e0e0e0' : '#333')
                      : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                    color: isDarkMode ? '#000' : '#fff',
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                    },
                  },
                }}
              >
                AR-AP
              </ToggleButton>
            </ToggleButtonGroup>
            </Box>
          </Box>
        )}

        {/* Top quick filters (summary cards + search) */}
        <Box sx={{ mb: topFiltersExpanded ? 2 : 0, display: areFilterSectionsCollapsed ? 'none' : 'block' }}>
          <Box
            onClick={() => setTopFiltersExpanded((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: topFiltersExpanded
                ? (isDarkMode ? '#64748b' : '#94a3b8')
                : (isDarkMode ? '#334155' : '#e2e8f0'),
              backgroundColor: topFiltersExpanded
                ? (isDarkMode ? 'rgba(51, 65, 85, 0.35)' : 'rgba(148, 163, 184, 0.12)')
                : 'transparent',
              transition: 'all 0.2s ease',
              mb: topFiltersExpanded ? 1.5 : 0,
              '&:hover': {
                borderColor: isDarkMode ? '#94a3b8' : '#64748b',
                backgroundColor: isDarkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(148, 163, 184, 0.18)',
              },
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
              Quick Filters
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500 }}
              >
                {topFiltersExpanded ? 'Collapse' : 'Expand'}
              </Typography>
              <IconButton
                size="small"
                aria-label={topFiltersExpanded ? 'Collapse quick filters' : 'Expand quick filters'}
                sx={{ p: 0.25 }}
              >
                {topFiltersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={topFiltersExpanded} timeout="auto" unmountOnExit>
            {/* Quick nav: same views as Work Orders drawer tabs */}
            <Box sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              overflowX: { xs: 'auto', md: 'visible' },
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              pb: { xs: 0.5, md: 0 },
            }}>
              {jobsQuickNavTabs.map(({ viewKey, label, path }) => {
                const selected = jobsView === viewKey;
                const selectedBg = isDarkMode ? '#fff' : '#000';
                const selectedFg = isDarkMode ? '#000' : '#fff';
                return (
                  <Box
                    key={viewKey}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(path)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(path);
                      }
                    }}
                    sx={{
                      flex: { xs: '0 0 auto', md: 1 },
                      minWidth: { xs: 120, md: 'auto' },
                      borderRadius: 2,
                      p: 2.5,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: selected
                        ? selectedBg
                        : (isDarkMode ? 'rgba(148, 163, 184, 0.22)' : '#e2e8f0'),
                      backgroundColor: selected
                        ? selectedBg
                        : (isDarkMode ? 'rgba(148, 163, 184, 0.06)' : 'rgba(0, 0, 0, 0.03)'),
                      color: selected ? selectedFg : 'text.secondary',
                      boxShadow: 'none',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                      '&:hover': {
                        backgroundColor: selected
                          ? (isDarkMode ? '#e8e8e8' : '#222')
                          : (isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(0, 0, 0, 0.06)'),
                        borderColor: selected
                          ? (isDarkMode ? '#e8e8e8' : '#222')
                          : (isDarkMode ? 'rgba(148, 163, 184, 0.35)' : '#cbd5e1'),
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: selected ? selectedFg : 'text.secondary',
                        fontSize: '0.875rem',
                        mb: 1,
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: selected ? selectedFg : 'text.primary',
                        fontSize: '2rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                      }}
                    >
                      {jobsTabCounts[viewKey]}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Search bar */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder={isDeletedView ? 'Search deleted work orders...' : 'Search work orders by title, client, or invoice #...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'background.paper',
                    fontSize: '0.875rem',
                    height: '42px',
                    '& input::placeholder': {
                      color: 'text.secondary',
                      opacity: 1,
                    },
                    '& fieldset': {
                      borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                      borderWidth: '1px',
                    },
                  }
                }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Status + advanced filters – collapsible section */}
        <Box sx={{ mb: statusFilterExpanded ? 2 : 0, display: areFilterSectionsCollapsed ? 'none' : 'block' }}>
          <Box
            onClick={() => setStatusFilterExpanded((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: statusFilterExpanded
                ? (isDarkMode ? '#64748b' : '#94a3b8')
                : (isDarkMode ? '#334155' : '#e2e8f0'),
              backgroundColor: statusFilterExpanded
                ? (isDarkMode ? 'rgba(51, 65, 85, 0.35)' : 'rgba(148, 163, 184, 0.12)')
                : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: isDarkMode ? '#94a3b8' : '#64748b',
                backgroundColor: isDarkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(148, 163, 184, 0.18)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                Status & Filters
              </Typography>
              {selectedStatuses.length > 0 && (
                <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  ({selectedStatuses.length} selected)
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500 }}
              >
                {statusFilterExpanded ? 'Collapse' : 'Expand'}
              </Typography>
              <IconButton
                size="small"
                aria-label={statusFilterExpanded ? 'Collapse status filters' : 'Expand status filters'}
                sx={{ p: 0.25 }}
              >
                {statusFilterExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={statusFilterExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
              {statusOptionsForCurrentView.map((option) => {
                const valueStr = String(option.value);
                const isSelected = selectedStatuses.includes(valueStr);
                const count = statusCountsByStatusId[valueStr] ?? 0;
                return (
                  <Chip
                    key={option.value}
                    label={`${option.label} (${count})`}
                    size="small"
                    icon={isSelected ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
                    onClick={() => handleStatusToggle(valueStr)}
                    variant="outlined"
                    sx={getStatusFilterChipSx(option.value, { selected: isSelected, isDarkMode })}
                  />
                );
              })}
            </Box>
            {/* Additional filters */}
            <Box sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              flexWrap: 'wrap',
              flex: 1,
              pt: 1,
            }}>
              {/* Priority Filter */}
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mr: 0.5 }}>
                  Priority:
                </Typography>
                {PRIORITY_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => handlePriorityToggle(String(opt.value))}
                    color={selectedPriorities.includes(String(opt.value)) ? 'primary' : 'default'}
                    variant={selectedPriorities.includes(String(opt.value)) ? 'filled' : 'outlined'}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      backgroundColor: selectedPriorities.includes(String(opt.value)) ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                      color: selectedPriorities.includes(String(opt.value)) ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                      borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      '&:hover': {
                        backgroundColor: selectedPriorities.includes(String(opt.value)) ? (isDarkMode ? '#e0e0e0' : '#333') : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Client Filter */}
              <Autocomplete
                multiple
                size="small"
                options={clientFilterOptions}
                value={selectedClients}
                onChange={(event, newValue) => {
                  setSelectedClients(newValue);
                }}
                getOptionLabel={(option) => (typeof option === 'object' && option?.client_name) ? option.client_name : String(option)}
                isOptionEqualToValue={(option, value) => (option?.id && value?.id) ? option.id === value.id : option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clients"
                    placeholder={selectedClients.length > 0 ? '' : 'Select clients...'}
                    sx={{
                      minWidth: { xs: '100%', sm: 180 },
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    const { bgColor, textColor, borderColor } = getClientChipStyles(option?.id);
                    return (
                      <Chip
                        key={key}
                        label={option?.client_name || option}
                        size="small"
                        {...chipProps}
                        sx={{
                          backgroundColor: bgColor,
                          color: textColor,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          height: '24px',
                          borderRadius: '6px',
                          border: `1px solid ${borderColor}`,
                          '& .MuiChip-label': {
                            padding: '0 8px',
                          },
                          '& .MuiChip-deleteIcon': {
                            fontSize: '16px',
                            ...(isDarkMode && {
                              color: textColor,
                              '&:hover': {
                                color: textColor,
                              },
                            }),
                          },
                        }}
                      />
                    );
                  })
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    {option?.client_name || option}
                  </li>
                )}
                sx={{
                  minWidth: { xs: '100%', sm: 180 },
                }}
              />

              {/* State Filter */}
              <Autocomplete
                multiple
                size="small"
                options={availableStates}
                value={selectedStates}
                onChange={(event, newValue) => {
                  setSelectedStates(newValue);
                }}
                getOptionLabel={(option) => states[option] || option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={states[option] || option}
                        size="small"
                        {...chipProps}
                        sx={{
                          '& .MuiChip-deleteIcon': {
                            fontSize: '16px',
                          },
                        }}
                      />
                    );
                  })
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    {states[option] || option}
                  </li>
                )}
                sx={{
                  minWidth: { xs: '100%', sm: 150 },
                }}
              />

              {/* Year Filter */}
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                <InputLabel id="year-filter-label">Year</InputLabel>
                <Select
                  labelId="year-filter-label"
                  id="year-filter"
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                  label="Year"
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    },
                  }}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year === 'all' ? 'All Years' : year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Trade Filter */}
              <Autocomplete
                multiple
                size="small"
                options={availableTrades}
                value={selectedTrades}
                onChange={(event, newValue) => {
                  setSelectedTrades(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trade"
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option}
                        size="small"
                        {...chipProps}
                        sx={{
                          '& .MuiChip-deleteIcon': {
                            fontSize: '16px',
                          },
                        }}
                      />
                    );
                  })
                }
                sx={{
                  minWidth: { xs: '100%', sm: 150 },
                }}
              />

              {/* Tags Filter */}
              <Autocomplete
                multiple
                size="small"
                options={tagFilterOptions}
                value={selectedTags}
                onChange={(event, newValue) => {
                  setSelectedTags(newValue);
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder={selectedTags.length > 0 ? '' : 'Select tags...'}
                    sx={{
                      minWidth: { xs: '100%', sm: 180 },
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.name}
                        size="small"
                        {...chipProps}
                        sx={{
                          ...getTagChipSx(option, theme),
                          fontSize: '0.75rem',
                          height: '24px',
                          '& .MuiChip-deleteIcon': {
                            fontSize: '16px',
                          },
                        }}
                      />
                    );
                  })
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.name}
                  </li>
                )}
                sx={{
                  minWidth: { xs: '100%', sm: 180 },
                }}
              />

              {/* Date Range Filter */}
              <DateRangePickerDropdown
                label="Date Range"
                placeholder="Select date range"
                startDate={selectedDateStart}
                endDate={selectedDateEnd}
                onStartChange={handleDateStartChange}
                onEndChange={handleDateEndChange}
                isDarkMode={isDarkMode}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={unlockedOnly}
                    onChange={(event) => setUnlockedOnly(event.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4caf50',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#4caf50',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                        backgroundColor: '#4caf50',
                      },
                    }}
                  />
                }
                label="Unlocked"
                sx={{
                  ml: 0.5,
                  mr: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: unlockedOnly ? 'success.main' : 'text.secondary',
                    fontWeight: unlockedOnly ? 600 : 400,
                  },
                }}
              />
            </Box>
          </Collapse>
        </Box>

        <Box sx={{
          display: areFilterSectionsCollapsed ? 'none' : 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          mt: areFilterSectionsCollapsed ? 0 : 0.5,
          mb: areFilterSectionsCollapsed ? 0.25 : 1.25,
        }}>
          {isMobile && !isDeletedView && (
            <IconButton
              onClick={handleColumnsPanelToggle}
              aria-label="Manage columns"
              size="small"
              sx={{
                border: '1px solid',
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                borderRadius: 1,
              }}
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          )}
          {!isDeletedView && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
              }
            }}
            size="small"
            sx={{
              height: '42px',
            }}
          >
            <ToggleButton 
              value="description"
              sx={{
                px: { xs: 1.5, md: 3 },
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: viewMode === 'description' ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                backgroundColor: viewMode === 'description' ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                '&:hover': {
                  backgroundColor: viewMode === 'description' 
                    ? (isDarkMode ? '#e0e0e0' : '#333') 
                    : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                },
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  },
                },
              }}
            >
              Description
            </ToggleButton>
            <ToggleButton 
              value="ar-ap"
              sx={{
                px: { xs: 1.5, md: 3 },
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: viewMode === 'ar-ap' ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                backgroundColor: viewMode === 'ar-ap' ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                '&:hover': {
                  backgroundColor: viewMode === 'ar-ap' 
                    ? (isDarkMode ? '#e0e0e0' : '#333') 
                    : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                },
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  },
                },
              }}
            >
              AR-AP
            </ToggleButton>
          </ToggleButtonGroup>
          )}
        </Box>
          </>
        )}

        {isDeletedView && (deletedJobsError || deletedJobDetailError) && (
          <Box sx={{
            color: 'text.primary',
            bgcolor: 'background.paper',
            padding: '16px',
            borderRadius: '8px',
            mb: 2,
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            border: '1px solid #ef4444',
            backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
          }}>
            {deletedJobsError || deletedJobDetailError}
          </Box>
        )}

        {/* AR-AP Error */}
        {viewMode === 'ar-ap' && arApError && (
          <Box sx={{
            color: 'text.primary',
            bgcolor: 'background.paper',
            padding: '16px',
            borderRadius: '8px',
            mb: 2,
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            border: isDarkMode ? '1px solid #ef4444' : '1px solid #ef4444',
            backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
          }}>
            <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '8px' }}>⚠️</span>
            {arApError}
          </Box>
        )}

        {/* Data Grid */}
        <Box sx={{ 
          backgroundColor: 'background.paper', 
          borderRadius: 2,
          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          overflow: 'hidden',
          position: 'relative',
          flex: 1,
          minHeight: 0,
          // Reserve right gutter so the columns panel does not cover grid/footer content.
          paddingRight: isMobile || isDeletedView ? 0 : '48px',
        }}>
          {/* Columns Panel Button (desktop rail) */}
          {!isMobile && !isDeletedView && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '48px',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              backgroundColor: 'background.paper',
              borderLeft: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            }}
          >
            <Button
              onClick={handleColumnsPanelToggle}
              sx={{
                minWidth: '48px',
                width: '48px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '12px 0',
                gap: 1,
                backgroundColor: 'transparent',
                color: 'text.primary',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                },
              }}
              aria-label="Manage columns"
            >
              <ViewColumnIcon sx={{ fontSize: '20px', mb: 0.5, color: 'text.primary' }} />
              <Typography
                sx={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  color: 'text.primary',
                }}
              >
                Columns
              </Typography>
            </Button>
          </Box>
          )}

          <JobsColumnsPopover
            open={columnsPanelOpen}
            anchorEl={columnsPanelAnchorEl}
            onClose={handleColumnsPanelClose}
            isDarkMode={isDarkMode}
            activeGridPreference={activeGridPreference}
            columnsByField={columnsByField}
            onToggleColumnVisibility={handleColumnVisibilityToggle}
            onReorderColumns={handleReorderColumns}
            onResetColumns={handleResetColumns}
          />

          <DataGrid
            rows={isDeletedView ? filteredDeletedJobs : filteredJobs}
            loading={isDeletedView ? deletedJobsLoading : (viewMode === 'ar-ap' ? arApLoading : loading)}
            columns={isDeletedView ? deletedColumns : displayColumns}
            density={isMobile ? 'compact' : 'standard'}
            columnVisibilityModel={isDeletedView ? undefined : activeGridPreference.columnVisibilityModel}
            onColumnVisibilityModelChange={isDeletedView ? undefined : handleColumnVisibilityModelChange}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10, 20, 30, 50, 100]}
            onRowClick={isDeletedView ? handleDeletedRowClick : handleRowClick}
            sx={{ 
              border: 0,
              height: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-columnHeaderTitle': { 
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-cell': { 
                fontSize: '0.875rem',
                color: 'text.primary',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
              },
              '& .MuiDataGrid-cell[data-field="description"]': {
                fontWeight: 600,
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                },
              },
              '& .MuiDataGrid-footerContainer': { 
                borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                position: 'relative',
                zIndex: 2,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Job details dialog */}
      <JobDetailsDialog
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        user={user}
        initialTabValue={initialJobDetailsTabValue}
      />

      <DeletedJobDetailsDialog
        selectedDeletedJob={selectedDeletedJob}
        setSelectedDeletedJob={(value) => {
          setSelectedDeletedJob(value);
          if (!value) setDeletedJobDetailError(null);
        }}
      />

      {/* Drawer to add a new job */}
      {canCreateOrEditJob(user) && (
        <AddJobDialog setAddJobDialogOpen={setDrawerOpen} addJobDialogOpen={drawerOpen} />
      )}

      <Dialog
        open={isNotesDialogOpen}
        onClose={handleCloseNotesDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            backgroundColor: 'background.paper',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            placeholder="Enter job notes..."
            disabled={notesUpdating}
            error={Boolean(notesDialogError)}
            helperText={notesDialogError || `${notesDraft.length}/500`}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseNotesDialog}
            disabled={notesUpdating}
            variant="outlined"
            sx={{
              borderColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#fff' : '#000',
              '&:hover': {
                borderColor: isDarkMode ? '#e0e0e0' : '#333',
                backgroundColor: 'transparent',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateNotes}
            disabled={notesUpdating || notesDraft.length > 500}
            variant="contained"
            sx={{
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
            }}
          >
            {notesUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
